import { useEffect, useState } from "react";
import {
  useFieldArray,
  // UseFieldArrayAppend,
  // UseFieldArrayRemove,
} from "react-hook-form";
import * as yup from "yup";

import {
  useDebounce,
  useHookForm,
  useMerchantApiMutation,
  useMerchantApiQuery,
  useMerchantApiQueryWithParams,
} from "../../../hooks";
import {
  IBank,
  IAddBankAccount,
  IHookForm,
  IModalFormWithTableProps,
  ISwAppSupportedCountry,
  ISwAppSupportedCurrency,
  IViewBankAccount,
  NoMeta,
} from "../../../interfaces-and-types";
import { globalHandlerUtils } from "../../../utils";

import { View } from "./View";

export interface IBankAccountFormViewProps
  extends IHookForm<IAddBankAccount & IBankAccountTagFormHelper>,
    IModalFormWithTableProps {
  isLoadingSupportedCountries: boolean;
  swAppSupportedCountries: ISwAppSupportedCountry[];

  isLoadingSupportedCurrencies: boolean;
  swAppSupportedCurrencies: ISwAppSupportedCurrency[];

  bankAccountTagsFieldArray: IAddBankAccount["bank_account_tags"];
  appendNewBankAccountTag: () => void;
  removeBankAccountTagByIndex: (index: number) => void;

  isLoadingBankList: boolean;
  bankList: IBank[];
  formValues: IAddBankAccount & IBankAccountTagFormHelper;
  onToggleSetPrimary: () => void;

  isBankAccountResolved: boolean;
  setBankAccountResolved: (isResolved: boolean) => void;
}

interface ICountrySelect {
  country: string;
}

interface ICountryAndCurrencySelect extends ICountrySelect {
  currency: string;
}

interface IBankAccountTagFormHelper {
  formHelperTagName?: string;
  formHelperTagDescription?: string | null;
}

export const BankAccountForm = (props: IModalFormWithTableProps) => {
  const [isBankAccountResolved, setBankAccountResolved] = useState(false);

  const {
    isLoading: isLoadingSupportedCountries,
    // isSuccess: isSuccessSupportedCountries,
    data: swAppSupportedCountries,
  } = useMerchantApiQuery<ISwAppSupportedCountry[]>("getSupportedCountries", {
    enabled: props.isModalOpen,
  });

  // const {
  //   isLoading: isLoadingSupportedCurrencies,
  //   // isSuccess: isSuccessSupportedCurrencies,
  //   data: swAppSupportedCurrencies,
  // } = useMerchantApiQuery<IViewCurrency[]>("getSupportedCurrencies", {
  //   enabled: props.isModalOpen,
  // });

  const { isSuccess: isSuccessSaveBankAccount, mutate: saveBankAccount } =
    useMerchantApiMutation<IAddBankAccount, IViewBankAccount>(
      "addBankAccount"
    ).usePostMutation;

  useEffect(() => {
    if (isSuccessSaveBankAccount) {
      reset();
      props.refreshTable();
      props.closeModal();
    }
  }, [isSuccessSaveBankAccount]);

  const initialFormValues: IAddBankAccount & IBankAccountTagFormHelper = {
    bank_account_tags: [],
    country: "",
    currency: "",
    bank_code: "",
    account_number: "",
    bvn: "",
    set_as_primary: false,

    formHelperTagName: "",
    formHelperTagDescription: null,
  };

  const saveBankAccountSchema: yup.SchemaOf<
    IAddBankAccount & IBankAccountTagFormHelper
  > = yup
    .object({
      bank_account_tags: yup.array().of(
        yup.object().shape({
          bankAccount: yup.string().nullable().optional(),
          tag_name: yup.string().required("Tag name is required"),
          description: yup.string().nullable().optional(),
        })
      ),

      formHelperTagName: yup.string().optional(),
      formHelperTagDescription: yup.string().nullable().optional(),

      country: yup.string().required("Country is required"),
      currency: yup.string().required("Currency is required"),
      bank_code: yup.string().required("Bank is required"),
      account_number: yup.string().required("Account Number is required"),
      bvn: yup
        .string()
        .nullable()
        .optional()
        .when("country", {
          is: (country: string) => country === "NG",
          then: yup.string().required("BVN is required"),
        }),
      set_as_primary: yup
        .boolean()
        .required("Set as primary status is required"),
    })
    .required();

  const handleApiSubmit = (data: IAddBankAccount) => {
    saveBankAccount(data);
  };

  const handleError = (errors: any) => globalHandlerUtils.handleError(errors);

  const {
    onFormSubmit,
    errors,
    register,
    reset,
    control,
    setValue,
    setError,
    clearErrors,
    watch,
  } = useHookForm({
    initialFormValues,
    validationSchema: saveBankAccountSchema,
    onValid: handleApiSubmit,
    onInvalid: handleError,
  });

  const formValues = watch();

  const onToggleSetPrimary = () =>
    setValue("set_as_primary", !formValues.set_as_primary);

  const debouncePeriodElapsed = useDebounce();

  // console.log(
  //   "debouncePeriodElapsed in BankAccountForm",
  //   debouncePeriodElapsed
  // );

  const { isLoading: isLoadingBankList, data: bankList } =
    useMerchantApiQueryWithParams<ICountryAndCurrencySelect, IBank[], NoMeta>(
      [
        "getBanks",
        {
          country: formValues.country,
          currency: formValues.currency,
        },
      ],
      {
        enabled:
          debouncePeriodElapsed &&
          props.isModalOpen &&
          !!formValues.country &&
          !!formValues.currency,
      }
    );

  const {
    isLoading: isLoadingSupportedCurrencies,
    data: swAppSupportedCurrencies,
  } = useMerchantApiQueryWithParams<
    ICountrySelect,
    ISwAppSupportedCurrency[],
    NoMeta
  >(
    [
      "getSupportedCurrencies",
      {
        country: formValues.country,
      },
    ],
    {
      enabled: props.isModalOpen && formValues.country !== "",
    }
  );

  // TODO: resolve issue this also resets the existing value when onboardingStatus is retrieved
  // clear bank selection on country change
  // useEffect(() => {
  //   reset({ ...formValues, bank_code: "", currency: "" });
  // }, [formValues.country]);

  const tagNameInForm = watch("formHelperTagName");

  useEffect(() => {
    clearErrors("formHelperTagName");
  }, [tagNameInForm]);

  const {
    fields: bank_account_tags,
    append: appendBankAccountTag,
    remove: removeBankAccountTag,
  } = useFieldArray({ name: "bank_account_tags", control });

  const removeBankAccountTagByIndex = (index: number) => {
    if (window.confirm("Are you sure you want to remove this tag?"))
      removeBankAccountTag(index);
  };

  const appendNewBankAccountTag = () => {
    if (!formValues.formHelperTagName) {
      setError("formHelperTagName", {
        message: "Tag name is required!",
      });
      return; // name required
    }

    if (
      formValues.bank_account_tags.find(
        (tg) => tg.tag_name === formValues.formHelperTagName
      )
    ) {
      setError("formHelperTagName", {
        message: "Duplicate tag name!",
      });
      return; // duplicate
    }

    appendBankAccountTag({
      tag_name: formValues.formHelperTagName,
      description: formValues.formHelperTagDescription,
    });

    // clear the add tag form
    setValue("formHelperTagName", "");
    setValue("formHelperTagDescription", null);
  };

  return (
    <View
      {...props}
      formValues={formValues}
      bankAccountTagsFieldArray={bank_account_tags}
      appendNewBankAccountTag={appendNewBankAccountTag}
      removeBankAccountTagByIndex={removeBankAccountTagByIndex}
      onToggleSetPrimary={onToggleSetPrimary}
      isLoadingSupportedCountries={isLoadingSupportedCountries}
      swAppSupportedCountries={swAppSupportedCountries || []}
      isLoadingSupportedCurrencies={isLoadingSupportedCurrencies}
      swAppSupportedCurrencies={swAppSupportedCurrencies || []}
      isLoadingBankList={isLoadingBankList}
      bankList={bankList || []}
      isBankAccountResolved={isBankAccountResolved}
      setBankAccountResolved={setBankAccountResolved}
      handleSubmit={onFormSubmit}
      register={register}
      errors={errors}
      control={control}
    />
  );
};
